import './employee-details.scss';
import React, { useState, useCallback, useEffect } from 'react';
import notify from 'devextreme/ui/notify';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import Button from 'devextreme-react/button';
import ScrollView from 'devextreme-react/scroll-view';
import { service } from '../../data/user-profile-service';
import { Avatar } from '../../components';
import { ProfileCard, ProfileCardItem } from '../../components/library/profile-card/ProfileCard';
import { withLoadPanel } from '../../utils/withLoadPanel';
import { useScreenSize } from '../../utils/media-query';
import { ChangeProfilePasswordForm } from '../../components/library/change-profile-password-form/ChangeProfilePasswordForm';
import { useAuth } from '../../contexts/auth';
import ODataStore from 'devextreme/data/odata/store';
import { useLocation } from 'react-router-dom';
import { updateEmployee } from '../../api/employees';
export interface State {
  employeeOid: string,
  backTo: string,
}
const copyToClipboard = (text) => (evt) => {
  window.navigator.clipboard?.writeText(text);
  const tipText = 'Text copied';
  notify(
    {
      message: tipText,
      minWidth: `${tipText.length + 2}ch`,
      width: 'auto',
      position: { of: evt.element, offset: '0 -30' }
    },
    'info',
    500
  );
};

type UserProfileContentProps = {
  basicInfoItems: ProfileCardItem[];
  contactItems: ProfileCardItem[];
  addressItems: ProfileCardItem[];
  profileData: Record<string, string>;
  handleDataChanged: () => void;
  handleChangePasswordClick: () => void;
  handleContentScrolled: (boolean) => void;
};

const UserProfileContent = ({
  basicInfoItems,
  contactItems,
  addressItems,
  profileData,
  handleDataChanged,
  handleChangePasswordClick,
  handleContentScrolled,
}: UserProfileContentProps
) => {
  const { isXSmall } = useScreenSize();

  const onScroll = useCallback((reachedTop) => {
    handleContentScrolled(reachedTop);
  }, [handleContentScrolled]);

  return (
    <ScrollView
      className='view-wrapper-scroll'
      onScroll={onScroll}
    >
      <div className='cards-container'>
        <ProfileCard
          wrapperCssClass='profile-card basic-info-card'
          title='Profile'
          colCount={4}
          cardData={profileData}
          items={basicInfoItems}
          onDataChanged={handleDataChanged}>
          <div className='basic-info-top-item profile-card-top-item'>
            <Avatar
              name={profileData?.FullName}
              backgroundColor={profileData?.SchedulerColor}
              size={75} />
            <div style={{ marginLeft: '10px' }}>
              <div className='title-text'>{profileData?.FullName}</div>
              <div className='subtitle-text with-clipboard-copy'>
                <span>ID: {profileData?.Oid.toString()}</span>
                <Button icon='copy'
                  className='copy-clipboard-button'
                  stylingMode='text'
                  onClick={copyToClipboard(profileData?.id)}
                  activeStateEnabled={false}
                  focusStateEnabled={false}
                  hoverStateEnabled={false}
                />
              </div>
              <Button
                text='Change Password'
                className='change-password-button'
                stylingMode='contained'
                icon={isXSmall ? void 0 : 'lock'}
                onClick={handleChangePasswordClick}
              />
            </div>
          </div>
        </ProfileCard>

        <ProfileCard
          wrapperCssClass='profile-card contacts-card'
          title='Contacts'
          cardData={profileData}
          items={contactItems}
          onDataChanged={handleDataChanged}>
          <div className='profile-card-top-item'>
            <div className='image-wrapper'>
              <i className='dx-icon dx-icon-mention' />
            </div>
            <div>
              <div className='title-text'>
                {profileData?.Email}
              </div>
              <div className='subtitle-text with-clipboard-copy'>
                {profileData?.Email}
                <Button
                  icon='copy'
                  className='copy-clipboard-button'
                  stylingMode='text'
                  onClick={copyToClipboard(profileData?.Email)}
                  activeStateEnabled={false}
                  focusStateEnabled={false}
                  hoverStateEnabled={false}
                />
              </div>
            </div>
          </div>
        </ProfileCard>

        <ProfileCard
          wrapperCssClass='profile-card address-card'
          title='Address'
          cardData={profileData}
          items={addressItems}
          onDataChanged={handleDataChanged}
        >
          <div className='profile-card-top-item'>
            <div className='image-wrapper'>
              <i className='dx-icon dx-icon-map' />
            </div>
            <div>
              <div className='title-text'>
                {profileData?.Address}, {profileData?.City}, {profileData?.PostalCode}
              </div>
            </div>
          </div>
        </ProfileCard>
      </div>
    </ScrollView>
  );
};

const UserProfileContentWithLoadPanel = withLoadPanel(UserProfileContent);

export const EmployeeDetails = () => {
  const { user, signOut } = useAuth();
  const state = useLocation().state as State;
  const [profileData, setProfileData] = useState<Record<string, string>>();
  const [savedProfileData, setSavedProfileData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isChangePasswordPopupOpened, setIsChangedPasswordPopupOpened] = useState(false);
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [basicInfoItems, setBasicInfoItems] = useState<ProfileCardItem[]>([]);
  const [contactItems, setContactItems] = useState<ProfileCardItem[]>([]);
  const [addressItems, setAddressItems] = useState<ProfileCardItem[]>([]);
  const [isContentScrolled, setIsContentScrolled] = useState(false);
  const employeesDataSource = new ODataStore({
    key: 'Oid',
    keyType: 'Guid',
    url: `${process.env.REACT_APP_URL}/api/odata/Employee`,
    version: 4,
    errorHandler: (error) => {
      if (error.httpStatus === 401) {
        signOut(true);
      }
    },
    beforeSend: (e) => {
      e.headers = {
        'Authorization': `Bearer ${user?.token}`
      };
    },
  });
  const dataChanged = useCallback(() => {
    setIsDataChanged(true);
  }, []);

  const changePassword = useCallback(() => {
    setIsChangedPasswordPopupOpened(true);
  }, []);

  const handleContentScrolled = useCallback((reachedTop) => {
    setIsContentScrolled(!reachedTop);
  }, []);

  const setSavedData = useCallback((data = profileData) => {
    setSavedProfileData(JSON.parse(JSON.stringify(data)));
  }, [profileData]);

  const onCancel = useCallback(() => {
    setProfileData(savedProfileData);
    setSavedData();
    setIsDataChanged(false);
  }, [savedProfileData, setSavedData]);

  const onSave = useCallback(() => {
    updateEmployee(state.employeeOid, profileData as any, user?.token ?? '').then(() => {
      notify(
        {
          message: 'Data saved',
          position: {
            at: 'bottom center',
            my: 'bottom center'
          }
        },
        'success'
      );
      setIsDataChanged(false);
      setSavedData();
    }).catch((error) => {
      notify(
        {
          message: `Failed to save data: ${error?.response?.data || error.message}`,
          position: {
            at: 'bottom center',
            my: 'bottom center'
          }
        },
        'error'
      );
    });
  }, [profileData, setSavedData, state.employeeOid, user?.token]);

  useEffect(() => {
    employeesDataSource.byKey(state.employeeOid).then((data) => {
      setProfileData(data);
      setSavedData(data);
    });

    Promise.all([
      employeesDataSource
    ]).then(() => {
      setIsLoading(false);
    });

    setBasicInfoItems(service.getBasicInfoItems());
    setContactItems(service.getContactItems());
    setAddressItems(service.getAddressItems());
  }, []);

  return <div className='view-host user-profile'>
    <div className='view-wrapper'>
      <Toolbar className={`theme-dependent ${isContentScrolled ? 'scrolled' : ''}`}>
        <Item location='before'>
          <div className='header-text'>Medewerker</div>
        </Item>
        <Item location='after'
          locateInMenu='never'>
          <Button
            className='cancel-button'
            text='Cancel'
            disabled={!isDataChanged}
            stylingMode='outlined'
            type='normal'
            onClick={onCancel}
          />
        </Item>
        <Item location='after'
          locateInMenu='never'>
          <Button
            disabled={!isDataChanged}
            text='Save'
            icon='save'
            type='default'
            stylingMode='contained'
            onClick={onSave}
          />
        </Item>
      </Toolbar>
      <UserProfileContentWithLoadPanel
        basicInfoItems={basicInfoItems}
        contactItems={contactItems}
        addressItems={addressItems}
        profileData={profileData}
        handleChangePasswordClick={changePassword}
        handleDataChanged={dataChanged}
        handleContentScrolled={handleContentScrolled}
        hasData={!isLoading}
        loading={isLoading}
        panelProps={{
          container: '.view-wrapper',
          position: { of: '.content' },
        }}
      />
    </div>

    {profileData?.Email && (
  <ChangeProfilePasswordForm
    email={profileData?.Email}
    visible={isChangePasswordPopupOpened}
    setVisible={setIsChangedPasswordPopupOpened}
  />
)}
  </div>;
};
